import { startRegistration } from '@simplewebauthn/browser';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { PageHeader } from '../components/PageHeader';
import { useSession } from '../session';

export function Account() {
  const { me, refresh } = useSession();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!me) return null;

  async function addPasskey() {
    setBusy(true);
    setError(null);
    try {
      const optionsJSON = await api<Parameters<typeof startRegistration>[0]['optionsJSON']>('/api/auth/passkeys/register/options', { method: 'POST' });
      const response = await startRegistration({ optionsJSON });
      await api('/api/auth/passkeys/register/verify', { method: 'POST', body: { response, name: name || undefined } });
      setName('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The passkey was not created');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setError(null);
    try {
      await api(`/api/auth/passkeys/${encodeURIComponent(id)}`, { method: 'DELETE' });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove that passkey');
    }
  }

  return (
    <>
      <PageHeader title="Your account" meta={<>{me.user.displayName ? `${me.user.displayName} · ` : ''}{me.user.email}</>} />
      {error && <div className="alert">{error}</div>}

      {/* The list and the way to add to it are separate cards, so the table can run edge to edge and the
          form is not a row squeezed under its last line. */}
      <h2 style={{ marginTop: 0 }}>Passkeys</h2>
      <div className="card flush">
        <table>
          <thead>
            <tr><th>Name</th><th>Added</th><th>Last used</th><th>Synced</th><th className="actions-cell"></th></tr>
          </thead>
          <tbody>
            {me.factors.passkeys.length === 0 && (
              <tr><td colSpan={5} className="empty">No passkeys yet.</td></tr>
            )}
            {me.factors.passkeys.map((k) => (
              <tr key={k.id}>
                <td>{k.name}</td>
                <td>{new Date(k.createdAt).toLocaleDateString()}</td>
                <td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}</td>
                <td><span className={`pill ${k.backedUp ? 'on' : ''}`}>{k.backedUp ? 'yes' : 'device only'}</span></td>
                <td className="actions-cell"><button className="danger" onClick={() => remove(k.id)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card">
        <h3>Add a passkey</h3>
        <p className="small muted">
          Your browser or phone will ask you to confirm. Keep at least two ways in: a synced passkey plus your
          authenticator, or two passkeys.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void addPasskey();
          }}
        >
          <div className="row">
            <input
              id="new-passkey-name"
              aria-label="Passkey name"
              placeholder="Name, e.g. Phone"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <button type="submit" disabled={busy}>{busy ? 'Waiting for your device…' : 'Add a passkey'}</button>
          </div>
        </form>
      </div>

      <h2>Authenticator app</h2>
      <div className="card">
        <p style={{ marginTop: 0 }}>
          <span className={`pill ${me.factors.totp ? 'on' : 'off'}`}>{me.factors.totp ? 'set up' : 'not set up'}</span>
        </p>
        <p className="small muted" style={{ marginBottom: 0 }}>
          To move to a new phone, ask an owner for a sign-in reset and enrol again.
        </p>
      </div>
    </>
  );
}
